import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiZap, FiTrendingUp, FiPlay, FiPause, FiVolume2, FiVolumeX, FiFileText } = FiIcons;

const Hero = ({ openLeadCapture }) => {
  const [videoError, setVideoError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => { setVideoError(true); });
    }
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) { videoRef.current.pause(); } else { videoRef.current.play(); }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden gradient-bg pt-20 pb-12 md:pt-24 md:pb-16">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <motion.div
            className="text-center lg:text-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center space-x-2 bg-gold/20 border border-gold rounded-full px-3 py-1.5 md:px-4 md:py-2 mb-4 md:mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
            >
              <span className="w-2 h-2 bg-gold rounded-full pulse-animation"></span>
              <span className="text-gold font-semibold text-xs md:text-sm">Financial Year End Limited — Ends Soon</span>
            </motion.div>

            <motion.h1
              className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 md:mb-6 text-gray-900"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Run unlimited, <span className="gradient-text">AI-powered webinars</span> that sell
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-gray-700 mb-6 md:mb-8 max-w-2xl mx-auto lg:mx-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Financial Year End prices for serious coaches — secure your spot at unbeatable pricing.
            </motion.p>

            <motion.div
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <div className="flex items-center justify-center lg:justify-start space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiUsers} className="text-primary-blue" />
                </div>
                <span className="text-sm font-medium text-gray-800">Unlimited Attendees</span>
              </div>
              <div className="flex items-center justify-center lg:justify-start space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiZap} className="text-primary-blue" />
                </div>
                <span className="text-sm font-medium text-gray-800">AI Automation</span>
              </div>
              <div className="flex items-center justify-center lg:justify-start space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiTrendingUp} className="text-primary-blue" />
                </div>
                <span className="text-sm font-medium text-gray-800">HD Streaming</span>
              </div>
            </motion.div>

            <motion.div
              className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start mb-6 md:mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <motion.button
                className="shake-shine-cta bg-primary-blue hover:bg-dark-blue text-white px-6 md:px-8 py-3 md:py-4 rounded-xl font-bold text-base md:text-lg transition-all border-2 border-gold"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Grab FY End Deal — ₹1,499
              </motion.button>
              <motion.button
                className="border-2 border-primary-blue hover:border-dark-blue text-primary-blue px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-base md:text-lg transition-all bg-white hover-glow flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={openLeadCapture}
              >
                <SafeIcon icon={FiFileText} />
                <span>Get More Results</span>
              </motion.button>
            </motion.div>

            <motion.div
              className="mt-6 md:mt-8 text-center lg:text-left"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              <p className="text-xs md:text-sm text-gray-600 mb-2">Trusted by 1000+ coaches • 7-day money-back guarantee</p>
              <div className="flex items-center justify-center lg:justify-start space-x-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-r from-primary-blue to-light-blue rounded-full border-2 border-white"></div>
                  ))}
                </div>
                <span className="text-xs md:text-sm text-gray-700">Join successful coaches</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className="hero-video-wrapper mt-8 lg:mt-0"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {!videoError ? (
              <div className="relative">
                <video
                  ref={videoRef}
                  preload="metadata"
                  poster="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=450&fit=crop"
                  className="w-full rounded-xl"
                  muted={isMuted}
                  autoPlay
                  loop
                  playsInline
                  onError={() => setVideoError(true)}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                >
                  <source src="https://storage.googleapis.com/msgsndr/Te8QGpu12zbMxZIVQlZi/media/65439835de92cb3ef77117f8.mp4" type="video/mp4" />
                </video>
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between bg-black/70 backdrop-blur-sm rounded-lg px-4 py-2">
                  <button onClick={togglePlay} className="text-white hover:text-gold transition-colors">
                    <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="text-xl md:text-2xl" />
                  </button>
                  <button onClick={toggleMute} className="text-white hover:text-gold transition-colors">
                    <SafeIcon icon={isMuted ? FiVolumeX : FiVolume2} className="text-xl md:text-2xl" />
                  </button>
                </div>
                <div className="absolute top-4 right-4 bg-gold text-white px-3 py-1 rounded-full text-xs font-bold">
                  Watch Demo
                </div>
              </div>
            ) : (
              <div className="relative bg-gradient-to-br from-primary-blue to-light-blue rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=450&fit=crop"
                  alt="Webinar Demo"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="text-center text-white">
                    <SafeIcon icon={FiPlay} className="text-4xl md:text-6xl mb-4 mx-auto" />
                    <p className="text-base md:text-lg font-semibold">Watch Our Demo</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;